import React, { useState } from 'react';

// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';

import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { CharterInventoryFilters as CharterInventoryFiltersType } from '~/components/Flights/Charter/charter.types';

import * as TimeService from '~/services/TimeService';

import {
  cabinOptions,
  charterInventoryCsvHeaders,
  defaultSelectedColumns,
  pageSizeOptions,
  stayOptions,
} from './charterInventoryOptions';

export type CharterInventoryFormState = {
  departureAirport: string;
  arrivalAirport: string;
  returnDepartureAirport: string;
  returnArrivalAirport: string;
  stayDurations: Array<number>;
  cabinClasses: CharterInventoryFiltersType['cabinClasses'];
  departingMonths: Array<number>;
  startDate: Date | null;
  endDate: Date | null;
};

type CharterInventoryFiltersProps = {
  value: CharterInventoryFormState;
  onChange: (nextValue: CharterInventoryFormState) => void;
  onSearch: () => void;
  onDownload: (columns: Array<string>) => void;
  canDownload: boolean;
  isSearching: boolean;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
};

const ensureValue = <T,>(candidate: Array<T>, fallback: Array<T>): Array<T> =>
  candidate.length ? candidate : fallback;

export function CharterInventoryFilters({
  value,
  onChange,
  onSearch,
  onDownload,
  canDownload,
  isSearching,
  pageSize,
  onPageSizeChange,
}: CharterInventoryFiltersProps) {
  const [selectedColumns, setSelectedColumns] = useState(defaultSelectedColumns);
  const update = (changes: Partial<CharterInventoryFormState>) => onChange({ ...value, ...changes });

  return (
    <Card>
      <CardHeader title="Filters" sx={{ pb: 0 }} />
      <CardContent>
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                select
                label="Departure airport"
                value={value.departureAirport}
                onChange={(event) => update({ departureAirport: event.target.value })}
                fullWidth
              >
                <MenuItem value="MEL">MEL</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                select
                label="Arrival airport"
                value={value.arrivalAirport}
                onChange={(event) => update({ arrivalAirport: event.target.value })}
                fullWidth
              >
                <MenuItem value="MLE">MLE</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                select
                label="Return departure airport"
                value={value.returnDepartureAirport}
                onChange={(event) => update({ returnDepartureAirport: event.target.value })}
                fullWidth
              >
                <MenuItem value="MLE">MLE</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                select
                label="Return arrival airport"
                value={value.returnArrivalAirport}
                onChange={(event) => update({ returnArrivalAirport: event.target.value })}
                fullWidth
              >
                <MenuItem value="MEL">MEL</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={stayOptions}
                value={stayOptions.filter((option) => value.stayDurations.includes(option.value))}
                getOptionLabel={(option) => option.label}
                onChange={(_, nextValue) =>
                  update({
                    stayDurations: ensureValue(
                      nextValue.map((option) => option.value),
                      stayOptions.map((option) => option.value),
                    ),
                  })
                }
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => <Chip {...getTagProps({ index })} key={index} label={option.label} />)
                }
                renderInput={(params) => <TextField {...params} label="Stay period" placeholder="Select stay" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={cabinOptions}
                value={cabinOptions.filter((option) => value.cabinClasses.includes(option.value))}
                getOptionLabel={(option) => option.label}
                onChange={(_, nextValue) =>
                  update({
                    cabinClasses: ensureValue(
                      nextValue.map((option) => option.value),
                      cabinOptions.map((option) => option.value),
                    ),
                  })
                }
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => <Chip {...getTagProps({ index })} key={index} label={option.label} />)
                }
                renderInput={(params) => <TextField {...params} label="Cabin class" placeholder="Select cabin" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <DatePicker
                label="Start date"
                value={value.startDate ? TimeService.toDayJs(value.startDate) : null}
                onChange={(newValue: Dayjs | null) => update({ startDate: newValue ? newValue.toDate() : null })}
                maxDate={value.endDate ? TimeService.toDayJs(value.endDate) : undefined}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <DatePicker
                label="End date"
                value={value.endDate ? TimeService.toDayJs(value.endDate) : null}
                onChange={(newValue: Dayjs | null) => update({ endDate: newValue ? newValue.toDate() : null })}
                minDate={value.startDate ? TimeService.toDayJs(value.startDate) : undefined}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} sx={{ flex: 1, maxWidth: '50%', width: '100%' }}>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={charterInventoryCsvHeaders}
                value={selectedColumns}
                onChange={(_, newValue) => setSelectedColumns(newValue)}
                renderInput={(params) => <TextField {...params} label="CSV columns" placeholder="Select columns" />}
                renderTags={(selected) => {
                  const count = selected.length;
                  if (count === 0) {
                    return null;
                  }
                  return <Chip size="small" label={`${count} selected`} sx={{ ml: 0.5 }} />;
                }}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={() => onDownload(selectedColumns)}
                disabled={!canDownload || isSearching || selectedColumns.length === 0}
                sx={{ whiteSpace: 'nowrap' }}
                fullWidth
              >
                Download CSV
              </Button>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Rows per page"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                sx={{ minWidth: 150, width: '100%' }}
              >
                {pageSizeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                color="primary"
                onClick={onSearch}
                sx={{ width: '100%' }}
                disabled={
                  isSearching ||
                  !value.departureAirport ||
                  !value.arrivalAirport ||
                  !value.returnDepartureAirport ||
                  !value.returnArrivalAirport
                }
              >
                Search
              </Button>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
